import { useCallback } from "react";
import { useOne } from "@refinedev/core";
import dayjs from "dayjs";
import { useSearchParams } from "react-router-dom";
import { useAnalyticsBackState } from "./useAnalyticsBack";
import {
  PresetDateRangePickerType,
  getRange,
} from "components/PresetDateRangePicker";
import {
  DEFAULT_ANALYTICS_DATE_RANGE,
  DEFAULT_ANALYTICS_DATE_RANGE_DAYS,
} from "../../constants";
import { MediaProjectResponse } from "../../types";
import { Maybe } from "types/";

export type AnalyticsDateRange = {
  type: PresetDateRangePickerType | null;
  startDate: Maybe<dayjs.Dayjs>;
  endDate: Maybe<dayjs.Dayjs>;
};

const VALID_TYPES: PresetDateRangePickerType[] = [
  "custom",
  "today",
  "yesterday",
  "7D",
  "30D",
  "2M",
  "6M",
  "12M",
  "XTD",
];

function parseDateRange(searchParams: URLSearchParams): AnalyticsDateRange {
  const type = searchParams.get("dateType") as PresetDateRangePickerType | null;

  if (type && VALID_TYPES.includes(type)) {
    if (type === "custom") {
      const from = searchParams.get("from");
      const to = searchParams.get("to");
      if (from && to && dayjs(from).isValid() && dayjs(to).isValid()) {
        return { type: "custom", startDate: dayjs(from), endDate: dayjs(to) };
      }
    } else {
      const [startDate, endDate] = getRange(type);
      return { type, startDate, endDate };
    }
  }

  return {
    type: DEFAULT_ANALYTICS_DATE_RANGE as PresetDateRangePickerType,
    startDate: dayjs().subtract(DEFAULT_ANALYTICS_DATE_RANGE_DAYS, "day"),
    endDate: dayjs(),
  };
}

export function useAnalyticsDateRange(projectId: string | undefined) {
  const [searchParams, setSearchParams] = useSearchParams();
  const { analyticsCanGoBack } = useAnalyticsBackState();

  // Ensure default dateType is always present in URL
  if (!searchParams.has("dateType")) {
    const defaults = new URLSearchParams(searchParams);
    defaults.set("dateType", DEFAULT_ANALYTICS_DATE_RANGE);
    setSearchParams(defaults, { replace: true, state: { analyticsCanGoBack } });
  }

  const dateRange = parseDateRange(searchParams);

  const setDateRange = useCallback(
    (
      range:
        | AnalyticsDateRange
        | ((prev: AnalyticsDateRange) => AnalyticsDateRange)
    ) => {
      setSearchParams(
        (prev) => {
          const resolved =
            typeof range === "function" ? range(parseDateRange(prev)) : range;

          const next = new URLSearchParams(prev);
          if (resolved.type) {
            next.set("dateType", resolved.type);
          } else {
            next.delete("dateType");
          }

          if (
            resolved.type === "custom" &&
            resolved.startDate &&
            resolved.endDate
          ) {
            next.set("from", resolved.startDate.format("YYYY-MM-DD"));
            next.set("to", resolved.endDate.format("YYYY-MM-DD"));
          } else {
            next.delete("from");
            next.delete("to");
          }

          return next;
        },
        { replace: true, state: { analyticsCanGoBack } }
      );
    },
    [setSearchParams, analyticsCanGoBack]
  );

  const { data: projectData } = useOne<MediaProjectResponse>({
    resource: `media/projects`,
    id: projectId,
  });

  const project = projectData?.data;

  return {
    dateRange,
    setDateRange,
    project,
  };
}
